import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FiArrowLeft, FiMapPin, FiUser, FiInfo, FiClock, FiTruck, FiAlertTriangle } from 'react-icons/fi';
import LoadingSpinner from '../components/common/LoadingSpinner';
import VehicleMap from '../components/map/VehicleMap';
import AlertsList from '../components/alerts/AlertsList';
import useVehicleTracking from '../hooks/useVehicleTracking';

const VehicleDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [vehicle, setVehicle] = useState(null);
  const [recentAlerts, setRecentAlerts] = useState([]);
  const [initialVehicles, setInitialVehicles] = useState([]);
  const { vehicles, alerts, isConnected } = useVehicleTracking(initialVehicles);

  useEffect(() => {
    // Fetch vehicle details
    const fetchVehicleDetails = async () => {
      try {
        setLoading(true);
        
        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Dummy vehicle data
        const dummyVehicle = {
          id,
          name: `Vehicle ${id}`,
          registration: 'ABC-1234',
          currentPosition: { lat: 40.7128, lng: -74.006, timestamp: new Date().toISOString() },
          currentSpeed: 35,
          status: 'active',
          lastUpdate: new Date().toISOString(),
          driver: { 
            name: 'John Doe', 
            phone: '555-1234',
            license: 'DL123456',
            email: 'john@example.com',
            image: 'https://randomuser.me/api/portraits/men/1.jpg' 
          },
          vehicleType: 'truck',
          fuelLevel: 75,
          lastMaintenance: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
          nextMaintenance: new Date(Date.now() + 60 * 24 * 60 * 60 * 1000).toISOString(),
          specifications: {
            make: 'Toyota',
            model: 'Hilux',
            year: 2020,
            engineType: 'Diesel',
            fuelCapacity: '80L',
            maxLoad: '1000kg'
          },
          owner: 'Fleet Company Inc.',
          insuranceExpiry: new Date(Date.now() + 180 * 24 * 60 * 60 * 1000).toISOString(),
          mileage: 25678,
          trips: [
            {
              date: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
              startLocation: 'Warehouse A',
              endLocation: 'Customer Site B',
              distance: 120,
              duration: 150, // minutes
              fuelUsed: 15
            },
            {
              date: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString(),
              startLocation: 'Customer Site B',
              endLocation: 'Warehouse A',
              distance: 122,
              duration: 155, // minutes
              fuelUsed: 16
            }
          ]
        };
        
        // Dummy alerts
        const dummyAlerts = [
          {
            id: 'a1',
            type: 'geofence_exit',
            vehicleId: id,
            timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
            message: 'Vehicle exited geofence Zone A',
            severity: 'warning',
            acknowledged: false
          },
          {
            id: 'a2',
            type: 'speeding',
            vehicleId: id,
            timestamp: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
            message: 'Vehicle exceeded speed limit (75mph in 55mph zone)',
            severity: 'high',
            acknowledged: true
          },
          {
            id: 'a3',
            type: 'low_fuel',
            vehicleId: id,
            timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
            message: 'Low fuel level alert (15%)',
            severity: 'medium',
            acknowledged: true
          }
        ];
        
        setVehicle(dummyVehicle);
        setRecentAlerts(dummyAlerts);
        setInitialVehicles([dummyVehicle]);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching vehicle details:', err);
        setLoading(false);
      }
    };

    fetchVehicleDetails();
  }, [id]);

  // Find the current vehicle data in the real-time tracking
  const currentVehicleData = vehicles.find(v => v.id === id);
  const vehicleAlerts = alerts.filter(a => a.vehicleId === id);
  
  // Combine initial vehicle data with real-time updates
  const currentVehicle = {
    ...vehicle,
    ...currentVehicleData
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-full">
        <LoadingSpinner />
      </div>
    );
  }

  if (!vehicle) {
    return (
      <div className="text-center py-10">
        <p className="text-ui-error">Vehicle not found</p>
        <button 
          className="mt-4 btn btn-primary" 
          onClick={() => navigate('/vehicles')}
        >
          Back to Vehicles
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-3">
          <button 
            onClick={() => navigate('/vehicles')}
            className="p-2 rounded-full hover:bg-gray-100"
          >
            <FiArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{vehicle.name}</h1>
            <p className="text-gray-600">Registration: {vehicle.registration}</p>
          </div>
        </div>
        
        {/* Connection status */}
        <div className="flex items-center text-sm">
          <span className={`inline-block w-3 h-3 rounded-full mr-2 ${isConnected ? 'bg-ui-success' : 'bg-ui-error'}`}></span>
          <span>{isConnected ? 'Connected' : 'Disconnected'}</span>
        </div>
      </div>

      {/* Vehicle Map and Status */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Map */}
        <div className="lg:col-span-2 card">
          <h2 className="text-lg font-semibold mb-4">Current Location</h2>
          <div className="h-96">
            <VehicleMap vehicles={[currentVehicle]} center={currentVehicle.currentPosition} />
          </div>
        </div>
        
        {/* Status and Info */}
        <div className="card space-y-6">
          <div>
            <h2 className="text-lg font-semibold mb-4">Vehicle Status</h2>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-500">Status:</span>
                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                  currentVehicle.status === 'active' ? 'bg-ui-success text-white' : 
                  currentVehicle.status === 'idle' ? 'bg-ui-warning text-gray-800' : 
                  'bg-ui-error text-white'
                }`}>
                  {currentVehicle.status === 'active' ? 'Active' : 
                   currentVehicle.status === 'idle' ? 'Idle' : 
                   currentVehicle.status === 'maintenance' ? 'Maintenance' : 'Unknown'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Current Speed:</span>
                <span>{currentVehicle.currentSpeed} mph</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Fuel Level:</span>
                <div className="flex items-center">
                  <div className="w-24 bg-gray-200 rounded-full h-2.5 mr-2">
                    <div className={`h-2.5 rounded-full ${currentVehicle.fuelLevel > 75 ? 'bg-ui-success' : 
                                                         currentVehicle.fuelLevel > 25 ? 'bg-ui-warning' : 
                                                         'bg-ui-error'}`} 
                         style={{ width: `${currentVehicle.fuelLevel}%` }}></div>
                  </div>
                  <span>{currentVehicle.fuelLevel}%</span>
                </div>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Last Updated:</span>
                <span className="flex items-center">
                  <FiClock className="mr-1" size={14} />
                  {new Date(currentVehicle.lastUpdate).toLocaleString()}
                </span>
              </div>
            </div>
          </div>
          
          <div>
            <h2 className="text-lg font-semibold mb-4">Driver Information</h2>
            <div className="flex items-center space-x-3 mb-3">
              <div className="w-12 h-12 rounded-full bg-gray-300 flex items-center justify-center">
                <FiUser size={24} />
              </div>
              <div>
                <h3 className="font-medium">{currentVehicle.driver?.name || 'Unassigned'}</h3>
                <p className="text-sm text-gray-500">{currentVehicle.driver?.phone || '-'}</p>
              </div>
            </div>
            {currentVehicle.driver && (
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">License:</span>
                  <span>{currentVehicle.driver.license}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Email:</span>
                  <span>{currentVehicle.driver.email}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Vehicle Details and Recent Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Details */}
        <div className="lg:col-span-2 card">
          <h2 className="text-lg font-semibold mb-4">Vehicle Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-medium text-gray-900 mb-3">General Information</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Make:</span>
                  <span>{currentVehicle.specifications?.make}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Model:</span>
                  <span>{currentVehicle.specifications?.model}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Year:</span>
                  <span>{currentVehicle.specifications?.year}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Engine:</span>
                  <span>{currentVehicle.specifications?.engineType}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Fuel Capacity:</span>
                  <span>{currentVehicle.specifications?.fuelCapacity}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Max Load:</span>
                  <span>{currentVehicle.specifications?.maxLoad}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Current Mileage:</span>
                  <span>{currentVehicle.mileage?.toLocaleString()} miles</span>
                </div>
              </div>
            </div>
            
            <div>
              <h3 className="font-medium text-gray-900 mb-3">Maintenance Information</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Last Maintenance:</span>
                  <span>{new Date(currentVehicle.lastMaintenance).toLocaleDateString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Next Maintenance:</span>
                  <span>{new Date(currentVehicle.nextMaintenance).toLocaleDateString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Owner:</span>
                  <span>{currentVehicle.owner}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Insurance Expiry:</span>
                  <span>{new Date(currentVehicle.insuranceExpiry).toLocaleDateString()}</span>
                </div>
              </div>
            </div>
          </div>
          
          <h3 className="font-medium text-gray-900 mt-6 mb-3">Recent Trips</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Route
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Distance
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Duration
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Fuel Used
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {currentVehicle.trips.map((trip, index) => (
                  <tr key={index}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {new Date(trip.date).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {trip.startLocation} → {trip.endLocation}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {trip.distance} miles
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {Math.floor(trip.duration / 60)}h {trip.duration % 60}m
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {trip.fuelUsed} liters
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        
        {/* Recent Alerts */}
        <div className="card">
          <h2 className="text-lg font-semibold mb-4">Recent Alerts</h2>
          <AlertsList alerts={vehicleAlerts.length > 0 ? vehicleAlerts : recentAlerts} />
          
          <Link to={`/alerts?vehicle=${id}`} className="mt-4 flex items-center justify-center text-primary-600 hover:text-primary-900">
            <span>View all alerts</span>
            <FiAlertTriangle className="ml-1" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default VehicleDetail;